import { apiService, toHttpError } from '@core/api';
import {
    type MemberNoteEditionInput,
    type MissionNoteEditionInput,
    type ResourceId,
} from '@wwf/sdk';
import type {
    DataProvider,
    GetListParams,
    GetManyReferenceParams,
    Identifier,
    RaRecord,
} from 'react-admin';
import {
    Page,
    queryStringFromListParams,
    toListParams,
    toRecord,
} from '../utils';

type ParentKey = 'memberId' | 'missionId';

type EditionInput = MemberNoteEditionInput | MissionNoteEditionInput;

export type NestedNotesProviderConfig = {
    parentKey: ParentKey;
    collection: 'members' | 'missions';
    createNote: (parentId: Identifier, input: EditionInput) => Promise<unknown>;
    updateNote: (
        parentId: Identifier,
        noteId: Identifier,
        input: EditionInput
    ) => Promise<unknown>;
};

function notesPath(
    collection: NestedNotesProviderConfig['collection'],
    parentId: Identifier
): string {
    return `/${collection}/${parentId}/notes`;
}

function resolveParentId(
    parentKey: ParentKey,
    sources: {
        id?: Identifier;
        filter?: Record<string, unknown>;
        data?: Record<string, unknown>;
        previousData?: RaRecord;
    }
): Identifier {
    const parentId =
        sources.id ??
        sources.filter?.[parentKey] ??
        sources.data?.[parentKey] ??
        sources.previousData?.[parentKey];

    if (parentId == null) {
        throw new Error(
            `Nested notes request requires ${parentKey} (path parent id)`
        );
    }
    return parentId;
}

function listParamsWithoutParent<
    T extends GetListParams | GetManyReferenceParams,
>(params: T, parentKey: ParentKey, referenceTarget?: string) {
    const filter = {
        ...(params.filter as Record<string, unknown> | undefined),
    };
    delete filter[parentKey];
    if (referenceTarget) {
        delete filter[referenceTarget];
    }
    return toListParams({ ...params, filter });
}

function toEditionInput(data: Record<string, unknown>): EditionInput {
    return {
        text: String(data.text ?? ''),
        attachmentIds:
            (data.attachmentIds as ResourceId[] | null | undefined) ??
            undefined,
    };
}

export function createNestedNotesProvider(
    config: NestedNotesProviderConfig
): Partial<DataProvider> {
    const { parentKey, collection, createNote, updateNote } = config;

    return {
        getList: async (_resource, params) => {
            const filter = params.filter as Record<string, unknown> | undefined;
            const parentId = resolveParentId(parentKey, { filter });
            const query = queryStringFromListParams(
                listParamsWithoutParent(params, parentKey)
            );

            try {
                const { data } = await apiService.get<Page<unknown>>(
                    `${notesPath(collection, parentId)}${query}`
                );
                return {
                    data: data.content.map(toRecord) as any[],
                    total: data.totalElements,
                };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        getManyReference: async (_resource, params) => {
            const parentId = resolveParentId(parentKey, { id: params.id });
            const query = queryStringFromListParams(
                listParamsWithoutParent(params, parentKey, params.target)
            );

            try {
                const { data } = await apiService.get<Page<unknown>>(
                    `${notesPath(collection, parentId)}${query}`
                );
                return {
                    data: data.content.map(toRecord) as any[],
                    total: data.totalElements,
                };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        create: async (_resource, params) => {
            const data = params.data as Record<string, unknown>;
            const parentId = resolveParentId(parentKey, { data });

            try {
                const note = await createNote(parentId, toEditionInput(data));
                return { data: toRecord(note) as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        update: async (_resource, params) => {
            const data = params.data as Record<string, unknown>;
            const previousData = params.previousData as RaRecord | undefined;
            const parentId = resolveParentId(parentKey, {
                data,
                previousData,
            });

            try {
                const note = await updateNote(
                    parentId,
                    params.id,
                    toEditionInput(data)
                );
                return { data: toRecord(note) as any };
            } catch (e) {
                throw toHttpError(e);
            }
        },

        updateMany: async (_resource, params) => {
            const data = params.data as Record<string, unknown>;
            const parentId = resolveParentId(parentKey, { data });

            try {
                const ids = await Promise.all(
                    params.ids.map(async id => {
                        await updateNote(parentId, id, toEditionInput(data));
                        return id;
                    })
                );
                return { data: ids };
            } catch (e) {
                throw toHttpError(e);
            }
        },
    };
}
